import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, CheckCircle, XCircle, AlertTriangle, RefreshCw,
  Download, ChevronDown, ChevronUp, Info, ArrowRight
} from 'lucide-react';
import api from '../../services/api';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

function StatusBadge({ status }) {
  const cfg = {
    not_started: { cls: 'bg-slate-100 text-slate-600',  label: 'Not Started' },
    running:     { cls: 'bg-blue-100  text-blue-700',   label: 'Running…'   },
    completed:   { cls: 'bg-green-100 text-green-700',  label: 'Complete'   },
    failed:      { cls: 'bg-red-100   text-red-700',    label: 'Failed'     },
    started:     { cls: 'bg-blue-100  text-blue-700',   label: 'Starting…'  },
  };
  const c = cfg[status] || cfg.not_started;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.cls}`}>
      {c.label}
    </span>
  );
}

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full bg-slate-200 rounded-full h-2.5">
      <div
        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function XeroImport() {
  const navigate = useNavigate();
  const [status, setStatus]         = useState(null);
  const [splits, setSplits]         = useState([]);
  const [splitsCount, setSplitsCount] = useState(0);
  const [showSplits, setShowSplits] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError]           = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/api/accounts/xero/import/status');
      setStatus(r.data);
    } catch (e) {
      console.error('Status fetch error', e);
    }
  }, []);

  // Poll while running
  useEffect(() => {
    fetchStatus();
    const interval = setInterval(() => {
      if (status?.is_running) fetchStatus();
    }, 2500);
    return () => clearInterval(interval);
  }, [fetchStatus, status?.is_running]);

  const handleStart = async () => {
    setTriggering(true);
    setError(null);
    try {
      await api.post('/api/accounts/xero/import');
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start import');
    } finally {
      setTriggering(false);
    }
  };

  const loadSplits = async () => {
    try {
      const r = await api.get('/api/accounts/xero/import/splits');
      setSplits(r.data.splits || []);
      setSplitsCount(r.data.count || 0);
      setShowSplits(true);
    } catch (e) {
      console.error('Splits fetch error', e);
    }
  };

  const downloadSplits = () => {
    if (!splits.length) return;
    const headers = ['Date','Description','Reference','Amount USD','Section Account','Related Accounts (Xero)','Mapped To','Type'];
    const rows = splits.map(s => [
      s.date, `"${s.description}"`, `"${s.reference}"`,
      s.amount_usd, `"${s.section_account}"`,
      `"${s.related_accounts}"`, `"${s.mapped_to}"`,
      s.is_debit ? 'Debit' : 'Credit',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'xero_splits_review.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCorrectSplit = (split) => {
    // Build pre-fill data for JournalEntries form:
    // Line 1: reverse the incorrectly-mapped account (full amount)
    // Line 2+: blank lines for each correct related account
    const correctAccounts = split.related_accounts
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const lines = [
      // Reversal line: flip the original Dr/Cr to undo the incorrect allocation
      {
        account_id:  '',
        description: `[Reverse] ${split.mapped_to}`,
        debit:  split.is_debit ? '' : String(split.amount_usd),
        credit: split.is_debit ? String(split.amount_usd) : '',
        _hint:  split.mapped_to,
      },
      // One blank line per correct account for the user to fill in
      ...correctAccounts.map(acct => ({
        account_id:  '',
        description: acct,
        debit:  split.is_debit ? '' : '',
        credit: split.is_debit ? '' : '',
        _hint:  acct,
      })),
    ];

    navigate('/accounts/usa/journal-entries', {
      state: {
        prefill: {
          date:        split.date,
          description: `Split Correction: ${split.description}`,
          reference:   split.reference,
          lines,
        },
      },
    });
  };
  const s = status;
  const isRunning  = s?.is_running || s?.status === 'running' || s?.status === 'started';
  const isDone     = s?.status === 'completed';
  const notStarted = !s || s.status === 'not_started';
  const progress   = s?.posted || 0;
  const total      = (s?.total_transactions || 0) - (s?.skipped_duplicate || 0);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Xero Historical Import</h1>
        <p className="text-sm text-slate-500 mt-1">
          Import 3 years of Eyvo USA transaction history from the Xero Account Transactions export
          into the SQL Server accounting ledger.
        </p>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800 space-y-1">
          <p className="font-medium">What this import does</p>
          <ul className="list-disc list-inside space-y-0.5 text-blue-700">
            <li>Reads <code className="bg-blue-100 px-1 rounded">xero_data.xlsx</code> — ~12,642 transaction rows across 89 accounts</li>
            <li>Deduplicates automatically (each transaction appears twice in Xero exports)</li>
            <li>Maps Xero account names to SQL Server Chart of Accounts</li>
            <li>Creates new accounts for Xero-specific entries not in your COA</li>
            <li><strong>Opening balances are skipped</strong> — you will enter the Dec 31, 2022 closing balances separately</li>
            <li>~277 split-account entries are flagged for your review but still imported (using first related account)</li>
          </ul>
        </div>
      </div>

      {/* Status card */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-slate-800">Import Status</h2>
            {s && <StatusBadge status={s.status || 'not_started'} />}
          </div>
          <button
            onClick={fetchStatus}
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
            data-testid="refresh-status-btn"
          >
            <RefreshCw size={14} className={isRunning ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Stats grid */}
        {s && s.status !== 'not_started' && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
            {[
              { label: 'Unique Transactions', value: s.total_transactions || 0, cls: 'text-slate-700' },
              { label: 'Posted to SQL',        value: s.posted || 0,             cls: 'text-green-700' },
              { label: 'Already Imported',     value: s.skipped_duplicate || 0,  cls: 'text-blue-600'  },
              { label: 'Failed',               value: s.failed || 0,             cls: 'text-red-600'   },
            ].map(({ label, value, cls }) => (
              <div key={label} className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-0.5">{label}</p>
                <p className={`text-xl font-bold ${cls}`}>{value.toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        {/* Progress bar */}
        {isRunning && total > 0 && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Posting transactions…</span>
              <span>{progress.toLocaleString()} / {total.toLocaleString()}</span>
            </div>
            <ProgressBar value={progress} max={total} />
          </div>
        )}

        {/* Completed summary */}
        {isDone && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-medium">Import complete!</p>
              <p>
                {s.posted?.toLocaleString()} transactions posted.{' '}
                {s.accounts_created > 0 && `${s.accounts_created} new accounts created. `}
                {s.splits_count > 0 && (
                  <span>
                    <strong>{s.splits_count}</strong> split-account entries need review.{' '}
                  </span>
                )}
                Your P&amp;L and Balance Sheet now reflect 3 years of Eyvo USA history.
              </p>
            </div>
          </div>
        )}

        {/* Error */}
        {s?.status === 'failed' && s?.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <XCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{s.error}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Action button */}
        <button
          onClick={handleStart}
          disabled={isRunning || triggering}
          data-testid="start-xero-import-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm
                     font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors"
        >
          <Upload size={15} />
          {isRunning || triggering
            ? 'Import Running…'
            : isDone
              ? 'Re-run Import (idempotent)'
              : 'Start Xero Import'}
        </button>
      </div>

      {/* Splits Report */}
      {(isDone || (s && s.splits_count > 0)) && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <button
            onClick={() => { if (!showSplits) loadSplits(); else setShowSplits(false); }}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
            data-testid="toggle-splits-btn"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" />
              <span className="text-sm font-semibold text-slate-800">
                Split-Account Entries Needing Review
              </span>
              {splitsCount > 0 && (
                <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full">
                  {splitsCount}
                </span>
              )}
            </div>
            {showSplits ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showSplits && splits.length > 0 && (
            <div className="border-t border-slate-100">
              <div className="flex items-center justify-between px-6 py-3 bg-amber-50">
                <p className="text-xs text-amber-800">
                  These {splits.length} transactions were split across multiple Xero accounts.
                  Only the first related account was used. Download the CSV to review and correct via manual journal entries.
                </p>
                <button
                  onClick={downloadSplits}
                  data-testid="download-splits-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300
                             text-amber-700 rounded-md text-xs font-medium hover:bg-amber-50 ml-4 flex-shrink-0"
                >
                  <Download size={12} />
                  CSV
                </button>
              </div>
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      {['Date','Description','Amount','Section Account','Related Accounts (Xero)','Mapped To',''].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {splits.map((s, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">{s.date}</td>
                        <td className="px-3 py-2 text-slate-800 max-w-xs truncate">{s.description}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">
                          {fmt(s.amount_usd)}
                        </td>
                        <td className="px-3 py-2 text-slate-600 max-w-xs truncate">{s.section_account}</td>
                        <td className="px-3 py-2 text-amber-700 max-w-xs truncate" title={s.related_accounts}>
                          {s.related_accounts}
                        </td>
                        <td className="px-3 py-2 text-slate-600 max-w-xs truncate">{s.mapped_to}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleCorrectSplit(s)}
                            title="Open Journal Entries form pre-filled with this split correction"
                            className="flex items-center gap-1 px-2 py-1 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded text-xs font-medium hover:bg-indigo-100 whitespace-nowrap"
                            data-testid={`correct-split-${i}`}
                          >
                            Correct <ArrowRight size={11} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Opening Balances note */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <p className="text-sm font-semibold text-slate-700 mb-1">Next Step: Opening Balances</p>
        <p className="text-sm text-slate-600">
          After the import, use the <strong>Journal Entries</strong> page to post your
          Dec 31, 2022 closing balances as a manual journal entry dated Dec 31, 2022.
          Debit each asset/expense account and credit the corresponding liability/equity/revenue
          accounts. Use account <strong>3000 — Opening Balance Equity</strong> as the balancing entry.
        </p>
      </div>
    </div>
  );
}
